/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Divider, Form, Image, Input, Popconfirm, Row, Space, Tooltip, Typography } from 'antd';
import MediaWidgetModal from '../../../Modals/MediaWidgetModal';
import CarouselGridCard from './CarouselGridCard';
import DraggableGrid from '../../../UI/DraggableGrid';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import { MediaGridIcon } from '../../../Icons/MediaGridIcon';
import { HorizontalListIcon } from '../../../Icons/HorizontalListIcon';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';
import { ArrowRightIcon } from '../../../Icons/ArrowRightIcon';
import { ArrowLeftIcon } from '../../../Icons/ArrowLeftIcon';
import { DeleteOutlined, PlusOutlined, PlusCircleOutlined } from '@ant-design/icons';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { v4 as uuidv4 } from 'uuid';

// Constant declarations
const { Meta } = Card;

const CarouselNodeForm = ({ form, selectedElement, propertyToFocus, showGrid }) => {
  const { t } = useTranslation();
  const { currentBotLanguage, isEditingPrimaryLanguage, primaryBotLanguage } = useBotLanguage();

  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isMediaWidgetOpen, setIsMediaWidgetOpen] = useState(null);
  const [isViewGrid, setIsViewGrid] = useState(showGrid);
  const [innerFocusIndex, setInnerFocusIndex] = useState(-1);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  // State and logic for animations
  const [animationClass, setAnimationClass] = useState('animate-fade-in');
  const prevCardIndexRef = useRef(currentCardIndex);

  useEffect(() => {
    if (propertyToFocus) {
      const cardValues = selectedElement.data?.cards || [];
      setIsLastSlide(cardValues.length === Number(propertyToFocus) + 1);
      setCurrentCardIndex(Number(propertyToFocus));
    }
  }, [propertyToFocus, selectedElement]);

  useEffect(() => {
    if (selectedElement && !propertyToFocus) {
      setIsLastSlide(!selectedElement.data?.cards || selectedElement.data?.cards?.length <= 1);
    }

    // If there are no buttons in any card, add a button array so that form fields exist
    if (selectedElement?.data?.cards && selectedElement.data.cards.length > 0) {
      selectedElement.data.cards.forEach((card, index) => {
        if (!card[currentBotLanguage]?.buttons || card[currentBotLanguage]?.buttons?.length === 0) {
          form.setFieldValue(['data', 'cards', index, currentBotLanguage, 'buttons'], []);
        }
      });
    }
  }, [selectedElement, isViewGrid, currentBotLanguage, form]);

  useEffect(() => {
    // When in grid layout, if we had an inner focus index set, move focus back once we switch to single card mode
    if (innerFocusIndex > -1 && !isViewGrid) {
      const cardValues = form.getFieldValue(['data', 'cards']);
      setCurrentCardIndex(innerFocusIndex);
      setIsLastSlide(cardValues?.length === Number(innerFocusIndex) + 1);
      setInnerFocusIndex(-1);
    }
  }, [innerFocusIndex, isViewGrid, form]);

  const getCurrentImage = (fieldName) => {
    return form.getFieldValue(['data', 'cards', fieldName, currentBotLanguage, 'imageUrl']);
  };

  const removeCurrentImage = (fieldName) => {
    form.setFieldValue(['data', 'cards', fieldName, currentBotLanguage, 'imageUrl'], null);
    // Workaround to notify the form that a change has been made (see comment in closeMediaWidgetModal)
    form.validateFields([['data', 'cards', fieldName, currentBotLanguage, 'imageUrl']]);
  };

  const openMediaWidgetModal = (id) => {
    setIsMediaWidgetOpen(id);
  };

  const closeMediaWidgetModal = (file) => {
    if (file) {
      if (file?.id) {
        form.setFieldValue(['data', 'cards', isMediaWidgetOpen, currentBotLanguage, 'imageUrl'], file.url);
      } else {
        form.setFieldValue(['data', 'cards', isMediaWidgetOpen, currentBotLanguage, 'imageUrl'], file);
      }
    }

    // Workaround to notify the form that a change has been made
    form.validateFields([['data', 'cards', isMediaWidgetOpen, currentBotLanguage, 'imageUrl']]);

    setIsMediaWidgetOpen(null);
  };

  const renderMask = (cardFieldName) => {
    return (
      <div
        className="w-100 h-100 align-content-center"
        onClick={() => {
          if (!isMediaWidgetOpen) {
            openMediaWidgetModal(String(cardFieldName));
          }
        }}
      >
        <Row justify="center">
          <Col>
            <Button
              type="link"
              size="large"
              className="p-0 text-white"
              onClick={(e) => {
                if (!isMediaWidgetOpen) {
                  openMediaWidgetModal(String(cardFieldName));
                }
              }}
              icon={<PlusCircleOutlined />}
            />
          </Col>

          {
            getCurrentImage(cardFieldName) ? (
              <Col>
                <Popconfirm
                  title={t('Are you sure you want to remove this image?')}
                  okText={t('Yes')}
                  cancelText={t('No')}
                  placement="topRight"
                  onConfirm={(e) => {
                    e.stopPropagation();
                    removeCurrentImage(cardFieldName);
                  }}
                  onCancel={(e) => e.stopPropagation()}
                >
                  <Button
                    type="link"
                    size="large"
                    className="p-0 text-white"
                    onClick={(e) => e.stopPropagation()}
                    icon={<DeleteIcon />}
                  />
                </Popconfirm>
              </Col>
            ) : null
          }
        </Row>
      </div>
    );
  };

  const handleDragEnd = (newItems) => {
    form.setFieldsValue({ data: { cards: newItems } });
    form.validateFields(['data', 'cards']); // Trigger validation to enable submit button
  };

  const cardValues = form.getFieldValue(['data', 'cards']) || [];
  const totalCards = cardValues.length;

  useEffect(() => {
    // Ensure that if currentCardIndex is out of range (e.g. after removing a card), we adjust it
    if (currentCardIndex >= totalCards && totalCards > 0) {
      setCurrentCardIndex(totalCards - 1);
      setIsLastSlide(true);
    } else {
      setIsLastSlide(currentCardIndex === totalCards - 1);
    }
  }, [currentCardIndex, totalCards]);

  // Animate on card index change
  useEffect(() => {
    if (prevCardIndexRef.current !== currentCardIndex) {
      // Fade out first
      setAnimationClass('animate-fade-out');
      const timer = setTimeout(() => {
        // After fade-out completes, fade in the new card
        setAnimationClass('animate-fade-in');
        prevCardIndexRef.current = currentCardIndex;
      }, 100); // Matches the animation duration
      return () => clearTimeout(timer);
    }
  }, [currentCardIndex]);

  const goToPrevCard = () => {
    form.validateFields().then(() => {
      setCurrentCardIndex((prev) => Math.max(prev - 1, 0));
    }).catch(() => {});
  };

  const goToNextCard = () => {
    form.validateFields().then(() => {
      setCurrentCardIndex((prev) => Math.min(prev + 1, totalCards - 1));
    }).catch(() => {});
  };

  const handleAddCard = (add) => {
    const cardValues = form.getFieldValue(['data', 'cards']) || [];

    form.validateFields().then(() => {
      // Add a new card
      add();

      // Move to the last added card
      setCurrentCardIndex(cardValues.length);
      setIsLastSlide(true);
    }).catch(() => {});
  };

  const renderNextArrow = (add) => {
    if (isLastSlide) {
      return (
        <Button
          size="large"
          type="primary"
          shape="circle"
          disabled={!isEditingPrimaryLanguage}
          onClick={() => handleAddCard(add)}
          icon={<PlusIcon />}
          style={{ top: 300 }}
        />
      );
    } else {
      return (
        <Button
          size="large"
          type="default"
          shape="circle"
          onClick={() => goToNextCard()}
          icon={<ArrowRightIcon />}
          style={{ top: 300 }}
        />
      );
    }
  };

  return (
    <Fragment>
      <Form.Item
        name={['data', 'currentBotLanguage']}
        initialValue={currentBotLanguage}
        noStyle
      />

      <Form.Item
        label={<Typography.Text type="secondary">{t('Set the question for the following response card options')}</Typography.Text>}
        name={['data', 'label', currentBotLanguage]}
      >
        <Input
          placeholder={t('Enter the question')}
          size="large"
          maxLength={998}
        />
      </Form.Item>

      <Divider />

      <Row className="mb-5 w-100" align="middle" justify="space-between">
        <Typography.Text strong>{t('Carousel Cards')}</Typography.Text>

        <Row>
          <Button
            type="text"
            className={classNames({ 'bg-skeleton': !isViewGrid })}
            icon={<MediaGridIcon height={25} width={25} className="icon-wrapper" />}
            onClick={() => setIsViewGrid(true)}
          />

          <Button
            type="text"
            className={classNames({ 'bg-skeleton': isViewGrid })}
            icon={<HorizontalListIcon />}
            onClick={() => setIsViewGrid(false)}
          />
        </Row>
      </Row>

      <Form.List name={['data', 'cards']}>
        {
          (cardFields, { add, remove }) => {
            if (isViewGrid) {
              return (
                <DraggableGrid
                  columns={2}
                  items={cardValues}
                  onDragEnd={handleDragEnd}
                  renderItem={(item, index) => (
                    <CarouselGridCard
                      item={item}
                      index={index}
                      primaryBotLanguage={primaryBotLanguage}
                      onEdit={() => {
                        setIsViewGrid(false);
                        setInnerFocusIndex(index);
                      }}
                      onRemove={() => remove(cardFields[index].name)}
                    />
                  )}
                  renderPlaceholder={() => (
                    <div className="px-3 mt-8">
                      <Button
                        type="dashed"
                        className="w-100 h-100 px-4 py-8"
                        onClick={() => {
                          add();
                          setInnerFocusIndex(cardFields.length);
                          setIsViewGrid(false);
                        }}
                      >
                        <Space direction="vertical" align="center">
                          <PlusIcon className="text-secondary fs-xl mt-0"/>
                          <Typography.Text className="text-secondary">{t('Add Card')}</Typography.Text>
                        </Space>
                      </Button>
                    </div>
                  )}/>
              );
            }

            // Not grid (horizontal single-card view)
            if (cardFields.length === 0) {
              return (
                <Row className="h-100 my-15" align="middle" justify="center">
                  <Col>
                    <Button
                      onClick={() => {
                        add();
                        setIsLastSlide(true);
                      }}
                      type="link"
                      disabled={!isEditingPrimaryLanguage}
                      className="fs-lg"
                    >
                      <Space direction="vertical">
                        <PlusIcon />
                        {t('Add your first response')}
                      </Space>
                    </Button>
                  </Col>
                </Row>
              );
            }

            const currentField = cardFields[currentCardIndex];
            if (!currentField) {
              return null;
            }

            return (
              <Fragment>
                <Form.Item key={currentField.key}>
                  <Form.Item
                    noStyle
                    name={[currentField.name, currentBotLanguage, 'id']}
                    initialValue={uuidv4()}
                    hidden
                  >
                    <Input />
                  </Form.Item>

                  <Row wrap={false} gutter={[12, 12]}>
                    <Col flex="none">
                      <Button
                        size="large"
                        type="default"
                        shape="circle"
                        disabled={currentCardIndex === 0}
                        onClick={() => goToPrevCard()}
                        icon={<ArrowLeftIcon />}
                        style={{ top: 300 }}
                      />
                    </Col>

                    <Col flex="auto">
                      <div className={animationClass} style={{ transition: 'all 0.1s ease' }}>
                        <Row justify="space-between">
                          <Col>
                            <Typography.Text>{t('Card')} {currentCardIndex + 1}</Typography.Text>
                          </Col>

                          <Col>
                            <Popconfirm
                              title={t('Are you sure you want to delete this card?')}
                              okText={t('Yes')}
                              cancelText={t('No')}
                              placement="topRight"
                              onConfirm={() => {
                                remove(currentField.name);
                              }}
                            >
                              <Tooltip title={t('Delete card')}>
                                <Button
                                  type="text"
                                  shape="circle"
                                  className="text-primary"
                                  disabled={!isEditingPrimaryLanguage}
                                  icon={<DeleteIcon />}
                                />
                              </Tooltip>
                            </Popconfirm>
                          </Col>
                        </Row>

                        <Card
                          hoverable={false}
                          className="mb-5"
                          cover={
                            <Image
                              key={getCurrentImage(currentField.name)}
                              style={{ height: 330 }}
                              src={getCurrentImage(currentField.name)}
                              className={classNames('object-fit-cover', {
                                placeholder: !getCurrentImage(currentField.name)
                              })}
                              preview={{
                                visible: false,
                                mask: renderMask(currentField.name)
                              }}
                              placeholder={!getCurrentImage(currentField.name)}
                            />
                          }
                        >
                          <Meta
                            description={
                              <Fragment>
                                <Row>
                                  <Col span={24}>
                                    <Form.Item
                                      label={t('Title')}
                                      name={[currentField.name, currentBotLanguage, 'title']}
                                      rules={[
                                        {
                                          required: currentBotLanguage === primaryBotLanguage,
                                          message: t('Title cannot be blank.')
                                        }
                                      ]}
                                    >
                                      <Input placeholder={t('Title')}/>
                                    </Form.Item>
                                  </Col>

                                  <Col span={24}>
                                    <Form.Item
                                      label={t('Description')}
                                      name={[currentField.name, currentBotLanguage, 'subtitle']}
                                    >
                                      <RichTextEditor
                                        index={currentField.name}
                                        className="fs-base"
                                        isScrollable={true}
                                        placeholder={t('Description')}
                                        shouldAutoFocus={false}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col span={24}>
                                    <Form.List
                                      name={[currentField.name, currentBotLanguage, 'buttons']}
                                      initialValue={[]}
                                    >
                                      {
                                        (buttonFields, { add: addButton, remove: removeButton }) => (
                                          <Row className="w-100 mt-2" direction="vertical">
                                            <Col span={24}>
                                              <Typography.Text className="mb-2">
                                                {t('Button Labels')}
                                              </Typography.Text>
                                            </Col>

                                            {
                                              buttonFields.map(({ key: buttonKey, name: buttonName }) => (
                                                <Fragment key={buttonKey}>
                                                  <Col span={24}>
                                                    <Form.Item
                                                      className="mb-2"
                                                      name={[buttonName, 'title']}
                                                      rules={[
                                                        {
                                                          required: currentBotLanguage === primaryBotLanguage,
                                                          message: t('Title cannot be blank.')
                                                        }
                                                      ]}
                                                    >
                                                      <Input
                                                        placeholder={t('Button')}
                                                        addonAfter={
                                                          <DeleteOutlined
                                                            className="cursor-pointer"
                                                            style={{ minWidth: 40 }}
                                                            onClick={() => removeButton(buttonName)}
                                                          />
                                                        }
                                                      />
                                                    </Form.Item>
                                                  </Col>

                                                  {/* Hidden fields */}
                                                  <Form.Item
                                                    name={[buttonName, 'type']}
                                                    hidden
                                                  >
                                                    <Input />
                                                  </Form.Item>

                                                  <Form.Item
                                                    name={[buttonName, 'tooltip']}
                                                    hidden
                                                  >
                                                    <Input />
                                                  </Form.Item>
                                                </Fragment>
                                              ))
                                            }

                                            <Col span={24}>
                                              <Button
                                                className="mt-2"
                                                type="dashed"
                                                hidden={buttonFields.length === 3}
                                                icon={<PlusOutlined className="text-muted"/>}
                                                onClick={() => addButton()}
                                              >
                                                <Typography.Text type="secondary">
                                                  {t('Add Button')}
                                                </Typography.Text>
                                              </Button>
                                            </Col>
                                          </Row>
                                        )
                                      }
                                    </Form.List>
                                  </Col>
                                </Row>
                              </Fragment>
                            }
                          />
                        </Card>
                      </div>
                    </Col>

                    <Col flex="none">
                      {renderNextArrow(add)}
                    </Col>
                  </Row>
                </Form.Item>

                <MediaWidgetModal
                  key={isMediaWidgetOpen}
                  onSuccess={closeMediaWidgetModal}
                  onClose={closeMediaWidgetModal}
                  isModalOpen={isMediaWidgetOpen}
                  isPublicToOrg={true}
                />
              </Fragment>
            );
          }
        }
      </Form.List>
    </Fragment>
  );
};

CarouselNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object,
  propertyToFocus: PropTypes.string,
  showGrid: PropTypes.bool
};

export default CarouselNodeForm;
